import React, { useEffect, useState } from "react";
import { BotbRAFContext } from "./context";
import { publishModalVisibility } from "../../redux/features/modal/modalSlice";
import { useAppDispatch } from "../../redux/hooks";
import useSmartico from "@/app/hooks/useSmartico";
import { getPreSubscriptionData, getRAFStatus, postRAFFtu } from "./service";
import { RAFStatus } from "@/app/(custom-header)/botb-refer-a-friend/types";
import { getHeaderData } from "@/app/(general-header)/header/header-service";
import { getCookie } from "@/helpers/cookies";

export enum PageState {
  NOT_AVAILABLE = "not-available",
  ONBOARDING = "onboarding",
  REWARDS = "rewards",
  SHARE_URL = "share-url",
}

const BotbRAFProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, setState] = useState<string>(null);
  const [showMoreDetails, setShowMoreDetails] = useState<{
    show: boolean;
    tab: number;
  }>({ show: false, tab: 1 });
  const [showStatistics, setShowStatistics] = useState<boolean>(false);
  const [statistics, setStatistics] = useState<any>([]);
  const [rafStatus, setRafStatus] = useState<RAFStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const api = useSmartico();
  const [headerData, setHeaderData] = useState<any>(null);
  const dispatch = useAppDispatch();
  const [preSubscriptionDataLoading, setPreSubscriptionDataLoading] = useState<boolean>(true);
  const [inviteFriendsPopup, setInviteFriendsPopup] = useState<boolean>(false);
  const [preSubscriptionData, setPreSubscriptionData] = useState<PreSubscriptionData>({
    features: [],
    header: {
      subtitlep1: "",
      subtitlep2: "",
      subtitlep3: "",
      title: "",
      video: "",
      videoMobile: "",
      legalText: "",
    },
    offer: {
      bonus: "",
      description: "",
      expireTime: 0,
      headline: "",
      offerImage: "",
    },
  });

  // Missions for smartico.
  const missionList = [39792, 39793, 39806, 39807, 39809, 39810, 39811];

  useEffect(() => {
    const isAuth = getCookie("BotB.Authenticated");
    if (isAuth !== "true") {
      location.href = `/login?ReturnUrl=${encodeURIComponent(location.pathname + location.search)}`;
      return;
    } else {
      api()
        .then(async res => {
          try {
            const missions = await res.getMissions();

            const filteredList = missions
              .filter(item => missionList.includes(item.id))
              .sort((a, b) => missionList.indexOf(a.id) - missionList.indexOf(b.id));
            setStatistics(filteredList);
          } catch (err) {
            console.error(err);
          }
        })
        .catch(err => console.error(err));
    }
  }, []);

  useEffect(() => {
    getHeaderData()
      .then(res => res.json())
      .then(res => {
        setHeaderData(res);
      })
      .catch(err => console.error(err));
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const response = await getRAFStatus();
        const status = (await response.json()) as RAFStatus;
        setRafStatus(status);
        if (status.programid === null) {
          setState(PageState.NOT_AVAILABLE);
        } else {
          setState(status.ftu ? PageState.ONBOARDING : PageState.REWARDS);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    getPreSubscriptionData(false, "default")
      .then(res => res.json())
      .then(res => {
        setPreSubscriptionData(res);
        setPreSubscriptionDataLoading(false);
      })
      .catch(err => console.error(err));
  }, []);

  const showStatisticsCLick = () => {
    setShowStatistics(true);
  };

  const congratsModalInvite = () => {
    setState(PageState.SHARE_URL);
  };

  const calculatePercentage = (referredFriends: number, maximumInvites: number): string => {
    if (maximumInvites === 0) return "0%";

    const percentage = Math.round((referredFriends / maximumInvites) * 100);
    return `${percentage}%`;
  };

  const closeRAFModal = () => {
    dispatch(publishModalVisibility({ isOpen: false }));
  };

  const setFTU = () => {
    postRAFFtu()
      .then(res => res.json())
      .then(res => {
        setState(PageState.REWARDS);
      })
      .catch(err => {
        setState(PageState.REWARDS);
      });
  };

  const viewDetailsClick = () => {
    setShowMoreDetails({ show: true, tab: 1 });
  };

  const showInviteFriendsPopup = () => {
    setInviteFriendsPopup(!inviteFriendsPopup);
  };

  return (
    <BotbRAFContext.Provider
      value={{
        minimumReferralsForGrantPrize: 5,
        state,
        setState,
        closeRAFModal,
        showMoreDetails,
        setShowMoreDetails,
        showStatistics,
        setShowStatistics,
        showInviteFriendsPopup,
        showStatisticsCLick,
        statistics,
        congratsModalInvite,
        rafStatus,
        loading,
        calculatePercentage,
        preSubscriptionDataLoading,
        preSubscriptionData,
        setFTU,
        userName: headerData?.MyAccountMenuBar?.FirstName || "",
        IsRAFEnabled: headerData?.IsRAFEnabled === true,
        viewDetailsClick,
        inviteFriendsPopup,
      }}>
      {children}
    </BotbRAFContext.Provider>
  );
};

export default BotbRAFProvider;
